/**
 * Merchant Shop Coupons (Phase 4 #5).
 *
 * Restaurant owners create coupons that apply ONLY to their own shop.
 * Every new coupon starts:
 *   - scope = "SHOP"
 *   - is_visible = false
 *   - status = "PENDING"
 *   - discount_charged_to = "SHOP" (the merchant is offering it from
 *     their own payout — tenant admin may flip to "TENANT" on approval).
 *
 * After admin approves (is_visible=true, status=ACTIVE), the coupon
 * shows up to customers at checkout.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Pencil, Trash2, Clock3, CheckCircle2, XCircle, Ticket, Camera, X } from "lucide-react";
import { api, inr } from "../../lib/api";
import { Field, Modal } from "../admin/AdminRestaurants";
import { toast } from "sonner";

const EMPTY = {
    code: "",
    title: "",
    description: "",
    discount_type: "PERCENT",
    discount_value: 10,
    min_cart_value: 199,
    max_discount: 50,
    restaurant_id: "",
};

function StatusBadge({ c }) {
    const approved = c.is_visible && c.status === "ACTIVE";
    if (approved) return (
        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-100 text-emerald-700">
            <CheckCircle2 size={11} /> Live
        </span>
    );
    if (c.status === "PENDING" || !c.is_visible) return (
        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-amber-100 text-amber-700">
            <Clock3 size={11} /> Pending approval
        </span>
    );
    return (
        <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-muted text-muted-foreground">
            <XCircle size={11} /> {c.status}
        </span>
    );
}

export default function MerchantCoupons() {
    const [rests, setRests] = useState([]);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(EMPTY);

    const load = () => api.get("/merchant/coupons").then(({ data }) => setRows(data));
    useEffect(() => {
        api.get("/merchant/restaurants").then(({ data }) => {
            setRests(data || []);
            if ((data || []).length === 1) setForm((f) => ({ ...f, restaurant_id: data[0].id }));
        });
        load();
    }, []);

    const shopName = useMemo(() => {
        const m = {};
        rests.forEach((r) => (m[r.id] = r.name));
        return m;
    }, [rests]);

    const openCreate = () => {
        setEdit(null);
        setForm({ ...EMPTY, restaurant_id: rests[0]?.id || "" });
        setOpen(true);
    };
    const openEdit = (c) => {
        setEdit(c);
        setForm({
            code: c.code, title: c.title || "", description: c.description || "",
            discount_type: c.discount_type, discount_value: c.discount_value,
            min_cart_value: c.min_cart_value, max_discount: c.max_discount ?? 0,
            restaurant_id: c.restaurant_id,
        });
        setOpen(true);
    };
    const save = async () => {
        if (!form.restaurant_id) return toast.error("Pick a shop");
        if (!form.code.trim()) return toast.error("Coupon code required");
        setBusy(true);
        try {
            const payload = {
                ...form,
                code: form.code.toUpperCase(),
                discount_value: Number(form.discount_value) || 0,
                min_cart_value: Number(form.min_cart_value) || 0,
                max_discount: Number(form.max_discount) || null,
            };
            if (edit) await api.patch(`/merchant/coupons/${edit.id}`, payload);
            else await api.post("/merchant/coupons", payload);
            toast.success(edit ? "Coupon updated" : "Sent for tenant approval");
            setOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };
    const remove = async (c) => {
        if (!confirm(`Delete coupon ${c.code}?`)) return;
        try {
            const { data } = await api.delete(`/merchant/coupons/${c.id}`);
            toast.success(data?.mode === "archived" ? "Archived" : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Shop Coupons</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} total · created coupons go to <strong>tenant admin for approval</strong></p>
                </div>
                <button data-testid="add-shop-coupon" onClick={openCreate} disabled={!rests.length} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50">
                    <Plus size={16} /> New coupon
                </button>
            </div>

            <InStoreRedemptionPanel />

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((c) => (
                    <div key={c.id} data-testid={`merchant-coupon-${c.code}`} className="bg-card border border-dashed border-primary/30 rounded-2xl p-4">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="font-display font-bold text-xl">{c.code}</p>
                                <p className="text-[11px] text-muted-foreground truncate">{shopName[c.restaurant_id] || "Shop"}</p>
                            </div>
                            <StatusBadge c={c} />
                        </div>
                        <p className="font-semibold text-sm mt-2 line-clamp-1">{c.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{c.description}</p>
                        <div className="mt-2 text-xs text-muted-foreground">
                            Min cart {inr(c.min_cart_value)} ·{" "}
                            {c.discount_type === "PERCENT" ? `${c.discount_value}% off` : c.discount_type === "FLAT" ? `${inr(c.discount_value)} off` : "Free delivery"}
                        </div>
                        <p className="text-[10px] mt-1.5 uppercase tracking-wider font-bold text-muted-foreground">
                            Charged to: <span className={c.discount_charged_to === "SHOP" ? "text-rose-700" : "text-sky-700"}>{c.discount_charged_to || "SHOP"}</span>
                        </p>
                        <div className="mt-3 flex items-center justify-end gap-1">
                            <button data-testid={`merchant-coupon-edit-${c.code}`} onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-muted"><Pencil size={14} /></button>
                            <button data-testid={`merchant-coupon-del-${c.code}`} onClick={() => remove(c)} className="p-2 rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
                {rows.length === 0 && (
                    <div className="sm:col-span-2 lg:col-span-3 text-center py-10 text-muted-foreground">No coupons yet. Click <strong>New coupon</strong> to create one.</div>
                )}
            </div>

            {open && (
                <Modal title={edit ? `Edit · ${edit.code}` : "New shop coupon"} onClose={() => setOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Shop">
                            <select data-testid="shop-coupon-restaurant" className="inp" value={form.restaurant_id} onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })}>
                                <option value="">Pick…</option>
                                {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Code"><input data-testid="shop-coupon-code" className="inp" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="GET50" /></Field>
                        <Field label="Type">
                            <select className="inp" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}>
                                <option value="PERCENT">PERCENT</option>
                                <option value="FLAT">FLAT</option>
                                <option value="FREE_DELIVERY">FREE_DELIVERY</option>
                            </select>
                        </Field>
                        <Field full label="Title"><input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                        <Field full label="Description"><input className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                        <Field label="Value"><input type="number" className="inp" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} /></Field>
                        <Field label="Max discount ₹"><input type="number" className="inp" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} /></Field>
                        <Field full label="Min cart value ₹"><input type="number" className="inp" value={form.min_cart_value} onChange={(e) => setForm({ ...form, min_cart_value: e.target.value })} /></Field>
                    </div>
                    <p className="mt-3 text-[11px] text-muted-foreground">
                        <strong>Note:</strong> by default the discount is deducted from <em>your shop&apos;s payout</em>. Tenant admin may flip this to &quot;TENANT absorbs&quot; on approval.
                    </p>
                    <button data-testid="save-shop-coupon" disabled={busy || !form.code || !form.restaurant_id} onClick={save} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={16} className="animate-spin" />} {edit ? "Save changes" : "Send for approval"}
                    </button>
                </Modal>
            )}
        </div>
    );
}



/**
 * In-store coupon redemption (iter43). Lets the merchant type the
 * customer's unlock code, validate it, and mark the coupon as redeemed.
 * Recent unlocks are listed below so the staff can confirm visually
 * before the customer presents the code.
 */
function InStoreRedemptionPanel() {
    const [code, setCode] = useState("");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [recent, setRecent] = useState(null);
    const [showRecent, setShowRecent] = useState(false);
    const [showScanner, setShowScanner] = useState(false);

    const loadRecent = async () => {
        try {
            const { data } = await api.get("/merchant/coupons/unlocks?limit=25");
            setRecent(data || []);
        } catch (err) { console.debug("[redeem-unlocks] list failed:", err); setRecent([]); }
    };
    useEffect(() => { if (showRecent && recent === null) loadRecent(); }, [showRecent, recent]);

    const redeem = async () => {
        const c = code.trim().toUpperCase();
        if (!c) return toast.error("Enter the customer's unlock code");
        setBusy(true);
        try {
            const params = new URLSearchParams({ code: c });
            if (amount) params.set("redeemed_amount", String(Number(amount) || 0));
            if (note) params.set("note", note);
            const { data } = await api.post(`/merchant/coupons/redeem-unlock?${params.toString()}`);
            setResult(data);
            if (data.already_redeemed) toast.info(`Code already redeemed on ${new Date(data.redeemed_at).toLocaleString()}`);
            else toast.success(`Redeemed ${data.coupon_code} for ${data.customer_mobile || data.customer_name || "customer"}`);
            setCode(""); setAmount(""); setNote("");
            if (recent !== null) loadRecent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not redeem");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-5 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border border-amber-300/60 rounded-2xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-amber-500 text-white inline-flex items-center justify-center shrink-0">
                        <Ticket size={16} />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold leading-tight">Redeem in-store unlock code</h2>
                        <p className="text-[11px] text-muted-foreground">Type the 8-char code the customer shows you</p>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="merchant-redeem-recent-toggle"
                    onClick={() => setShowRecent((s) => !s)}
                    className="text-[11px] font-semibold bg-white border border-amber-300 rounded-full px-3 py-1 text-amber-800"
                >
                    {showRecent ? "Hide" : "Show"} recent unlocks
                </button>
            </div>

            <div className="mt-3 grid sm:grid-cols-[1.2fr_auto_1fr_1.5fr_auto] gap-2 items-stretch">
                <input
                    data-testid="merchant-redeem-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="UNLOCK-CODE"
                    maxLength={12}
                    className="inp font-mono uppercase tracking-widest text-center font-bold"
                />
                <button
                    type="button"
                    data-testid="merchant-redeem-scan-btn"
                    onClick={() => setShowScanner(true)}
                    title="Scan customer's QR code"
                    className="bg-white border border-amber-300 text-amber-700 font-bold rounded-xl px-3 inline-flex items-center justify-center gap-1.5"
                >
                    <Camera size={14} /> Scan
                </button>
                <input
                    data-testid="merchant-redeem-amount-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Amount discounted (₹, opt)"
                    type="number"
                    className="inp"
                />
                <input
                    data-testid="merchant-redeem-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note (optional, e.g. receipt #)"
                    className="inp"
                />
                <button
                    data-testid="merchant-redeem-submit"
                    onClick={redeem}
                    disabled={busy || !code}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl px-4 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                    {busy && <Loader2 size={14} className="animate-spin" />} Redeem
                </button>
            </div>

            {result && (
                <div data-testid="merchant-redeem-result" className={`mt-3 rounded-xl p-3 text-sm ${result.already_redeemed ? "bg-amber-100 border border-amber-300 text-amber-800" : "bg-emerald-100 border border-emerald-300 text-emerald-800"}`}>
                    <p className="font-bold">
                        {result.already_redeemed ? "Already redeemed" : "Redeemed successfully"} · <span className="font-mono">{result.coupon_code}</span>
                    </p>
                    <p className="text-xs mt-0.5">
                        Customer {result.customer_mobile || result.customer_name || "—"} ·{" "}
                        {result.discount_type === "PERCENT" ? `${result.discount_value}% off`
                            : result.discount_type === "FLAT" ? `${inr(result.discount_value)} off`
                                : "Free delivery"}
                    </p>
                </div>
            )}

            {showRecent && (
                <div className="mt-3" data-testid="merchant-redeem-recent">
                    {recent === null && <p className="text-xs text-muted-foreground inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading recent unlocks…</p>}
                    {recent && recent.length === 0 && <p className="text-xs text-muted-foreground">No unlocks yet from customers for your shops.</p>}
                    {recent && recent.length > 0 && (
                        <div className="bg-white/70 rounded-xl divide-y divide-amber-200/60">
                            {recent.map((r) => (
                                <div key={r.id} className="flex items-center justify-between px-3 py-2 text-xs">
                                    <div className="min-w-0">
                                        <p className="font-mono font-bold">{r.unlock_code}</p>
                                        <p className="text-[10px] text-muted-foreground truncate">{r.coupon_code} · {r.customer_mobile || r.customer_name || "—"}</p>
                                    </div>
                                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${r.status === "REDEEMED" ? "bg-emerald-100 text-emerald-700" : r.status === "UNLOCKED" ? "bg-amber-200 text-amber-800" : "bg-muted text-muted-foreground"}`}>
                                        {r.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {showScanner && (
                <QrScannerModal
                    onClose={() => setShowScanner(false)}
                    onDecoded={(decoded) => {
                        // ``decoded`` is the raw QR payload — strip the
                        // ``bizil-unlock:`` URI prefix so the input
                        // shows just the 8-char code. The backend also
                        // tolerates the prefix as a safety net.
                        let c = String(decoded || "").trim().toUpperCase();
                        if (c.startsWith("BIZIL-UNLOCK:")) c = c.split(":", 2)[1].trim();
                        setCode(c);
                        setShowScanner(false);
                        toast.success(`Scanned: ${c}`);
                    }}
                />
            )}
        </div>
    );
}

/**
 * Camera-driven QR scanner. Loads ``html5-qrcode`` lazily so it doesn't
 * bloat the merchant bundle for shops that never scan. Auto-prefers
 * the back-facing camera and stops the stream cleanly on unmount /
 * close.
 */
function QrScannerModal({ onClose, onDecoded }) {
    const [error, setError] = useState("");
    const [starting, setStarting] = useState(true);
    const elemId = "merchant-redeem-qr-reader";

    useEffect(() => {
        let scanner;
        let cancelled = false;
        (async () => {
            try {
                const mod = await import("html5-qrcode");
                const Html5Qrcode = mod.Html5Qrcode;
                if (cancelled) return;
                scanner = new Html5Qrcode(elemId, /* verbose */ false);
                await scanner.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 240, height: 240 },
                        aspectRatio: 1.0,
                    },
                    (decoded) => {
                        // Stop ASAP — html5-qrcode keeps firing the
                        // callback after the first decode otherwise.
                        try { scanner.pause(/* shouldPauseVideo */ true); } catch (e) { /* race */ }
                        onDecoded(decoded);
                    },
                    (_err) => { /* per-frame decode misses — silent */ },
                );
                setStarting(false);
            } catch (e) {
                console.error("[QrScannerModal] init failed:", e);
                setError(e?.message || "Couldn't access the camera. Check permissions.");
                setStarting(false);
            }
        })();
        return () => {
            cancelled = true;
            if (scanner) {
                scanner.stop().then(() => scanner.clear()).catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="merchant-redeem-scanner">
            <div className="bg-card w-full max-w-md rounded-3xl p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-base inline-flex items-center gap-2">
                        <Camera size={16} className="text-amber-600" /> Scan unlock QR
                    </h3>
                    <button
                        onClick={onClose}
                        data-testid="merchant-redeem-scanner-close"
                        className="w-8 h-8 rounded-full hover:bg-muted inline-flex items-center justify-center"
                    ><X size={16} /></button>
                </div>
                <div className="relative aspect-square bg-black rounded-2xl overflow-hidden">
                    <div id={elemId} className="w-full h-full" />
                    {starting && !error && (
                        <div className="absolute inset-0 inline-flex items-center justify-center text-white text-sm gap-2">
                            <Loader2 size={16} className="animate-spin" /> Starting camera…
                        </div>
                    )}
                    {error && (
                        <div className="absolute inset-0 p-4 inline-flex items-center justify-center text-white text-sm text-center">
                            {error}
                        </div>
                    )}
                </div>
                <p className="text-[11px] text-muted-foreground text-center mt-3">
                    Point at the customer's QR. The code will auto-fill when detected.
                </p>
            </div>
        </div>
    );
}
